import { DeleteOutlined, EditOutlined, MoreOutlined } from "@ant-design/icons"
import {
  Avatar,
  Button,
  Card,
  Dropdown,
  Flex,
  Tag,
  Tooltip,
  Typography
} from "antd"
import React from "react"

import { empty } from "../../../../../../../constants/placeholders"
import type { AlbumListItem } from "../../../../../../Album/types/album.types.ts"
import { AlbumThumbnails } from "./AlbumThumbnails.tsx"

const { Text, Paragraph } = Typography

interface AlbumCardProps {
  album: AlbumListItem
  onView?: (id: string) => void
  userId: string
  deleteAllowed: boolean
  deleteForeignAllowed: boolean
  onDeleteClick: (albumId: string) => void
  editAllowed: boolean
  onEditClick: (albumId: string) => void
}

export const AlbumCard = ({
  album,
  onView,
  userId,
  deleteAllowed,
  deleteForeignAllowed,
  onDeleteClick,
  editAllowed,
  onEditClick
}: AlbumCardProps) => {
  const isVisible = album?.is_visible_to_customer
  const count = album?.attachments_count || 0
  const created = album?.created
  const deleteAlbumAllowed =
    deleteForeignAllowed || (deleteAllowed && userId === album?.created_by)

  const onViewHandler = () => {
    onView?.(album?.id)
  }

  return (
    <Card
      onDoubleClick={onViewHandler}
      hoverable
      className="h-full"
      styles={{ body: { padding: 0 } }}
    >
      <AlbumThumbnails attachments={album?.attachments} />

      <Tooltip title={`${isVisible ? "Visible" : "Hidden"} for customer`}>
        <Tag
          color={isVisible ? "green" : "red"}
          className="m-0 absolute top-2 left-2"
        >
          {isVisible ? "Visible" : "Hidden"}
        </Tag>
      </Tooltip>

      {(editAllowed || deleteAlbumAllowed) && (
        <Dropdown
          trigger={["click"]}
          menu={{
            items: [
              ...(editAllowed
                ? [{ key: "edit", label: "Edit", icon: <EditOutlined /> }]
                : []),
              ...(deleteAlbumAllowed
                ? [
                    {
                      key: "delete",
                      label: "Delete",
                      icon: <DeleteOutlined />,
                      danger: true
                    }
                  ]
                : [])
            ],
            onClick: ({ key, domEvent }) => {
              domEvent.stopPropagation()
              if (key === "edit") onEditClick(album.id)
              else if (key === "delete") onDeleteClick(album.id)
            }
          }}
        >
          <Button
            className="m-0 absolute top-2 right-2"
            icon={<MoreOutlined />}
            onClick={(e) => e.stopPropagation()}
          />
        </Dropdown>
      )}

      <div className="p-2.5">
        <Flex justify="space-between" align="start" className="mb-1">
          <Text ellipsis={{ tooltip: album?.name }} strong className="block">
            {album?.name}
          </Text>
        </Flex>

        <Paragraph
          ellipsis={{
            rows: 2,
            expandable: "collapsible",
            symbol: (expanded) => (expanded ? "less" : "more")
          }}
          type="secondary"
          className="block mb-1 text-sm"
        >
          {album?.description || empty}
        </Paragraph>

        <Flex align="center" gap={8} className="mb-3">
          <Text type="secondary" className="text-xs">
            {created}
          </Text>
          <Text type="secondary" className="text-xs">
            •
          </Text>
          <Text type="secondary" className="text-xs">
            {count} file(s)
          </Text>
          {/*<Text type="secondary" className="text-xs">*/}
          {/*  •*/}
          {/*</Text>*/}
          {/*<Text type="secondary" className="text-xs">*/}
          {/*  Order: {album.order}*/}
          {/*</Text>*/}
        </Flex>

        <Flex justify="space-between" gap={3} align="center">
          {album?.created_by_name && (
            <Flex align="center" className={"overflow-hidden"} gap={8}>
              <Avatar className={"shrink-0"} size="small">
                {album?.created_by_name?.charAt(0)}
              </Avatar>
              <Text
                ellipsis={{ tooltip: album?.created_by_name }}
                type="secondary"
                className="text-sm"
              >
                {album?.created_by_name}
              </Text>
            </Flex>
          )}
          <Button
            type="link"
            size="small"
            className="p-0 ml-auto"
            onClick={onViewHandler}
          >
            View Album
          </Button>
        </Flex>
      </div>
    </Card>
  )
}
